import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import {
	getPermissionGroupsV2,
	deletePermissionGroup,
	getRightsForUser,
} from "../actions/permissionsActions";

import CustomizableTable from "./Tables/CustomizableTable";
import { MAIN_COLUMN_WIDTH, ID_COLUMN_WIDTH } from "./Tables/SortableTable";
import InteractiveTableCell from "./Tables/InteractiveTableCell";
import { CellWithPills } from "./Tables/CellWithPills";
import { ButtonWithIcon, ButtonWithRouter } from "./ButtonWithIcon";
import { IconUsers } from "@tabler/icons-react";
import { updateUserSetting } from "../actions/userSettingsActions";
import { encodeFullpath, filmCreditsFlexHelper } from "../utilities/utilities";
import { RowCheckbox } from "./RowCheckbox";
import { DropDownButton } from "./DropDownButton";
import { LexWorkplaceGavelIcon } from "./LexWorkplaceIcons";
import MaterialTabs from "./MaterialTabs";
import UsersDropDownSelect from "./UsersDropDownSelect";
import GroupRights from "./GroupRights";
import { defaultOrderBy } from "../utilities/tableSortUtils";

const tableUniqueID = "/permissions";

const tabUrlMap = {
	"list/": {
		index: 0,
		customColumnsKey: "/permissions/group/list",
		breadcrumbsIcon: "user",
	},
	"calculate/": {
		index: 1,
	},
};

const tabMap = [
	{ label: "Manage Groups", url: "list" },
	{ label: "Calculate Rights", url: "calculate" },
];

const userOptionsMap = (user) => ({
	...user,
	value: user.sub,
	label: user.displayName,
});

const mapUsersKeepMeFixed = (users, sub, isFirmAdminGroup) => {
	//the isFixed property hides the x button next to a dropdown list item
	return users.map((user) =>
		isFirmAdminGroup && user.sub === sub
			? { ...userOptionsMap(user), isFixed: true }
			: userOptionsMap(user)
	);
};

const groupsOnTopOrderedBy = (arr, funcs, dirs, indexKey) => {
	function sortGroups(a, b) {
		// force null and undefined to the bottom
		a = a === null || a === undefined ? "" : a;
		b = b === null || b === undefined ? "" : b; // force any string values to lowercase

		a = typeof a === "string" ? a.toLowerCase() : a;
		b = typeof b === "string" ? b.toLowerCase() : b; // Return either 1 or -1 to indicate a sort priority

		if (a > b) {
			return 1;
		}

		if (a < b) {
			return -1;
		} // returning 0, undefined or any falsey value will use subsequent sorts or
		// the index as a tiebreaker

		return 0;
	}
	return arr.sort((rowA, rowB) => {
		const aGroup =
			rowA._original.type === "FIRM_ADMIN" ||
			rowA._original.type === "ALL_TENANT_USERS";
		const bGroup =
			rowB._original.type === "FIRM_ADMIN" ||
			rowB._original.type === "ALL_TENANT_USERS";
		for (let i = 0; i < funcs.length; i += 1) {
			const comp = funcs[i];
			const desc = dirs[i] === false || dirs[i] === "desc";
			const sortInt = comp(rowA, rowB);
			if (aGroup && !bGroup) {
				return -1;
			}
			if (!aGroup && bGroup) {
				return 1;
			}
			if (aGroup && bGroup) {
				const groupSortInt = sortGroups(
					rowA._original.name,
					rowB._original.name
				);
				return groupSortInt;
			}
			if (sortInt) {
				return desc ? -sortInt : sortInt;
			}
		}
		// Use the row index for tie breakers
		return dirs[0]
			? rowA[indexKey] - rowB[indexKey]
			: rowB[indexKey] - rowA[indexKey];
	});
};

class PermissionsGroupList extends Component {
	constructor(props, context) {
		super(props, context);
		this.state = {
			filter: "Open",
			lastLocation: {
				list: "list/",
				calculate: "calculate/",
			},
			calculatedUserRights: false,
		};
		this.switchedTab = this.switchedTab.bind(this);
		const makeGroupThreeDotMenuItems = (row) => {
			var menu = [
				{
					text: "Edit Group",
					href: "/permissions/groups/edit/" + row.original.id,
				},
			];
			menu = menu.concat([
				{
					text: "Delete Group",
					onSelect: () =>
						this.props.deletePermissionGroup(row.original.id),
					disabled:
						row.original.type === "FIRM_ADMIN" ||
						row.original.type === "ALL_TENANT_USERS",
				},
			]);
			return menu;
		};
		this.columnConfig = [
			{ Header: "ID", accessor: "id", width: ID_COLUMN_WIDTH },
			{
				Header: "Group Name",
				accessor: "name",
				width: MAIN_COLUMN_WIDTH,
				Cell: (row) => (
					<InteractiveTableCell
						menuItems={makeGroupThreeDotMenuItems(row)}
					>
						<span>
							<Link
								to={`/permissions/groups/edit/${row.original.id}`}
							>
								{row.value}
							</Link>
							{row.original.type !== "REGULAR" ? (
								<CellWithPills
									value={{
										text: "",
										pills: [
											{
												label: "System Group",
												style: {
													backgroundColor:
														"var(--lxw-main-400)",
												},
												title: "System Group",
											},
										],
									}}
								/>
							) : null}
						</span>
					</InteractiveTableCell>
				),
				wildcard: true,
			},
			{
				Header: "Group Members",
				accessor: "members",
				Cell: (row) =>
					row.original.type === "ALL_TENANT_USERS" ? (
						<span>Everyone</span>
					) : row.original.members ? (
						row.original.members
							.map((x) => x.displayName)
							.join(", ")
					) : (
						""
					),
				filterValueMethod: (x) =>
					x.members.map((gm) => gm.displayName).join(", "),
			},
		];
	}
	componentDidMount() {
		this.props.getPermissionGroupsV2();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.userRights !== this.props.userRights) {
			this.setState({ calculatedUserRights: this.props.userRights });
		}
	}

	toggleDefaultPermissionGroupsOnTop() {
		this.props.updateUserSetting(
			"defaultPermissionGroupsOnTop",
			!this.props.defaultPermissionGroupsOnTop
		);
	}

	static getDerivedStateFromProps(props, state) {
		var lastLocation = { ...state.lastLocation };
		lastLocation[props.match.params.root] = encodeFullpath(
			props.match.params.root + "/"
		);
		return { ...state, lastLocation };
	}

	switchedTab(value) {
		const newURL =
			"/permissions/groups/" + this.state.lastLocation[tabMap[value].url];
		this.props.history.push(newURL);
	}

	render() {
		return (
			<div className="fullHeightFlex noDefaultMargin">
				<MaterialTabs
					style={{ height: "48px", margin: 0 }}
					activeTab={
						tabUrlMap[this.props.match.params.root + "/"].index
					}
					tabs={tabMap}
					switchedTab={this.switchedTab}
				/>
				{this.props.match.params.root === "list" && (
					<CustomizableTable
						actionButtons={({ openCsvExport }) => (
							<div style={{ display: "flex", gap: 8 }}>
								<ButtonWithRouter
									redirect={"/permissions/groups/create"}
									buttonText="New Group"
									iconComponent={
										<IconUsers
											size={16}
											style={{
												verticalAlign: "middle",
											}}
										/>
									}
								/>
								<DropDownButton
									title="Actions"
									pullRight
									iconComponent={<LexWorkplaceGavelIcon />}
									menuItems={[
										{
											text: filmCreditsFlexHelper(
												"System Groups on Top (A-Z)",
												<RowCheckbox
													checked={
														this.props
															.defaultPermissionGroupsOnTop
													}
												/>
											),
											onSelect: () =>
												this.toggleDefaultPermissionGroupsOnTop(),
										},
										{
											text: "Export Data as CSV",
											onSelect: openCsvExport,
										},
									]}
								/>
							</div>
						)}
						leftSideFilterSpace={
							<div
								style={{
									display: "flex",
									marginTop: 8,
									alignItems: "center",
									gap: 8,
								}}
							>
								<IconUsers
									size={24}
									style={{
										verticalAlign: "middle",
										marginLeft: 8,
									}}
								/>
								<span
									style={{
										fontSize: "1.5em",
										marginTop: 6,
									}}
								>
									Groups
								</span>
							</div>
						}
						orderByMethod={
							this.props.defaultPermissionGroupsOnTop
								? groupsOnTopOrderedBy
								: defaultOrderBy
						}
						csvExport={{
							fileName: "permission-groups",
							csvColumnRenderers: {
								members: (val, row) =>
									row.type === "ALL_TENANT_USERS"
										? "Everyone"
										: val
											? val
													.map((x) => x.displayName)
													.join("; ")
											: "",
							},
						}}
						className="fullWidth"
						loading={this.props.loading}
						data={this.props.availablePermissionGroups}
						defaultSorted={[{ id: "name" }]}
						autoResize
						columns={this.columnConfig}
						noResultsText={"No Groups"}
						tableUniqueID={tableUniqueID}
					/>
				)}
				{this.props.match.params.root === "calculate" && (
					<>
						<div
							style={{
								padding: 16,
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								justifyContent: "center",
								gap: 16,
								maxWidth: 800,
								width: "100%",
							}}
						>
							<div style={{ flex: 1, maxWidth: 600 }}>
								<UsersDropDownSelect
									label="Select User"
									name="selectedUser"
									isAsync
									isClearable
									value={this.state.selectedUser}
									defaultValue={
										this.props.editMode &&
										this.props.focusedPermissionGroup
											.members
											? mapUsersKeepMeFixed(
													this.props
														.focusedPermissionGroup
														.members,
													this.props.cognitoSub,
													this.props.isFirmAdminGroup
												)
											: []
									}
									options={mapUsersKeepMeFixed(
										this.props.users,
										this.props.cognitoSub,
										this.props.isFirmAdminGroup
									)}
									handler={(key, value) => {
										if (Array.isArray(value)) {
											this.setState({
												[key]: false,
												calculatedUserRights: false,
											});
										} else {
											this.setState({
												[key]: value,
												calculatedUserRights: false,
											});
										}
									}}
								/>
							</div>
							{/* button that will be enabled when a user is selected */}
							<ButtonWithIcon
								style={{ marginTop: 8 }}
								disabled={!this.state.selectedUser}
								className="blue"
								handler={() =>
									this.props.getRightsForUser(
										this.state.selectedUser.sub
									)
								}
								buttonText="CALCULATE RIGHTS"
							/>
						</div>
						{this.state.calculatedUserRights && (
							<div
								style={{ maxWidth: "1000px", margin: "0 10px" }}
							>
								<GroupRights
									viewingUserCalculatedRights={true}
									rights={this.state.calculatedUserRights}
									userRightsMessage={`${
										this.state.selectedUser
											? this.state.selectedUser
													.displayName
											: "User"
									} has the following rights based on their group memberships:`}
								/>
							</div>
						)}
					</>
				)}
			</div>
		);
	}
}
PermissionsGroupList.propTypes = {
	loading: PropTypes.bool.isRequired,
};
const mapStateToProps = (state) => {
	return {
		currentPathname: state.globalSearchbar.location,
		myUserID: state.user.userID,
		loading: state.request.loading,
		cognitoSub: state.user.cognitoUser.attributes.sub,
		availablePermissionGroups: state.permissions.permissionGroups,
		defaultPermissionGroupsOnTop:
			state.userSettings.defaultPermissionGroupsOnTop,
		users: state.permissions.users,
		userRights: state.permissions.userRights,
	};
};
export default connect(mapStateToProps, {
	getPermissionGroupsV2,
	getRightsForUser,
	deletePermissionGroup,
	updateUserSetting,
})(PermissionsGroupList);
